<template>
    <div class="notranslate">
        <template v-if="isPrefix">
            <span>{{ currencySymbolInfo.symbol }}</span>
            <span v-if="currencySymbolInfo.hasSpace">&nbsp;</span>
        </template>
        <div ref="elRef" />
        <template v-if="!isPrefix">
            <span v-if="currencySymbolInfo.hasSpace">&nbsp;</span>
            <span>{{ currencySymbolInfo.symbol }}</span>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import { SymbolPosition } from '@sports-utils/money';
    import Decimal from 'decimal.js-light';
    import Odometer from 'odometer';
    import { computed, onMounted, toRefs, useTemplateRef, watch } from 'vue';
    import 'odometer/themes/odometer-theme-minimal.css';
    import { useCustomerStore } from '@/store/customerStore';

    const props = defineProps<{
        value: number;
    }>();

    const { value } = toRefs(props);

    const elRef = useTemplateRef('elRef');

    const { currencySymbolInfo, moneySeparators } = useCustomerStore();
    const { thousandSeparator, decimalSeparator } = moneySeparators;

    const isPrefix = computed(() => currencySymbolInfo.position === SymbolPosition.Start);

    // to handle case where odometer will trim trailing 0 in X.X0,
    // we add additional 0.001 to value and render with 3 decimal places,
    // then hide last decimal place through css
    const valueWithExtraThirdDecimalPlace = computed(() => new Decimal(value.value).add(0.001).toNumber());

    let instance: Odometer | null = null;

    onMounted(() => {
        const el = elRef.value;
        if (!el) return;

        // Set odometer options locally for this instance
        window.odometerOptions = { auto: false };

        instance = new Odometer({
            el,
            value: valueWithExtraThirdDecimalPlace.value,
            format: `(${thousandSeparator}ddd)${decimalSeparator}ddd`, // render with 3 decimal places, then hide last decimal place through css
        });
    });

    watch(valueWithExtraThirdDecimalPlace, () => {
        if (instance) {
            instance.update(valueWithExtraThirdDecimalPlace.value);
        }
    });
</script>

<style lang="scss" scoped>
:deep(.odometer-radix-mark + .odometer-digit + .odometer-digit ~ .odometer-digit) {
    display: none;
}
</style>
